/**
 * TalkingAvatar — illustrated SVG persona with viseme lip sync, idle motion and moods.
 *
 * Framework-agnostic: mount it into any element, then drive the mouth from one of
 *   avatar.attachNode(ctx, node, speakers)  // live audio already inside an AudioContext (amplitude + spectral guess)
 *   avatar.speakAlong(sentence)             // no audio access: text-derived visemes, timed at speaking pace
 *   avatar.playVisemeTrack(track, t0)       // precise visemes from TTS alignment (Azure / ElevenLabs)
 *
 * Viseme names: rest, AA, E, O, U, M, F, L   (mapAzureViseme / mapChar convert to them)
 * Moods:        neutral, smile, thinking, concern
 *
 * To restyle the character, replace svgTemplate(); keep the data-id hooks and the mouth centre at
 * (210, 248) in the 420×420 viewBox, or update the constants in drawMouth() and tick().
 */

export type Viseme = 'rest' | 'AA' | 'E' | 'O' | 'U' | 'M' | 'F' | 'L';
export type Mood = 'neutral' | 'smile' | 'thinking' | 'concern';
export interface VisemeEvent { t: number; v: Viseme; a?: number }
export type AvatarColors = Record<keyof typeof DEFAULT_COLORS, string>;

interface MouthShape { w: number; o: number; r: number; t: number }
interface MoodShape { smile: number; browY: number; browTilt: number; lidTop: number }

const VIS: Record<Viseme, MouthShape> = {
  rest: { w: 22, o: 1.5, r: 0, t: 0 },
  AA: { w: 24, o: 14, r: 0.25, t: 0.6 },
  E: { w: 28, o: 7, r: 0, t: 1 },
  O: { w: 14, o: 12, r: 1, t: 0.2 },
  U: { w: 11, o: 7, r: 1, t: 0 },
  M: { w: 21, o: 0, r: 0, t: 0 },
  F: { w: 24, o: 3, r: 0, t: 1 },
  L: { w: 23, o: 9, r: 0.1, t: 0.5 },
};
const MOOD: Record<Mood, MoodShape> = {
  neutral: { smile: 0, browY: 0, browTilt: 0, lidTop: 0 },
  smile: { smile: 6, browY: -1, browTilt: 0, lidTop: 0.15 },
  thinking: { smile: -1, browY: -3, browTilt: 1, lidTop: 0.2 },
  concern: { smile: -4, browY: 1, browTilt: -1, lidTop: 0.05 },
};
const DEFAULT_COLORS = {
  wall1: '#f1e6d4', wall2: '#d9c7ab', stripe: '#b4863c', desk: '#cdb48c',
  skin: '#e8b89a', skinShade: '#d79c7c', hair: '#2b2420', coat: '#223a5e',
  lips: '#b4645e', mouth: '#5a1f26', teeth: '#f6f1ea', eye: '#2b2420', cheek: '#e29a86', shirt: '#f6f1ea',
};
const HOOKS = ['head', 'body', 'pupils', 'lids', 'brows', 'lipOuter', 'mouthInner', 'mouthClipPath', 'teethTop'] as const;
// Text-to-speech runs at roughly this many characters a second.
const CHARS_PER_SECOND = 14;

/** Azure Speech viseme IDs (0–21) → our shapes */
export function mapAzureViseme(id: number): Viseme {
  const map: Record<number, Viseme> = {
    0: 'rest', 1: 'AA', 2: 'AA', 3: 'O', 4: 'E', 5: 'E', 6: 'E', 7: 'U', 8: 'O', 9: 'AA', 10: 'O', 11: 'AA',
    12: 'E', 13: 'O', 14: 'L', 15: 'L', 16: 'L', 17: 'L', 18: 'F', 19: 'L', 20: 'L', 21: 'M',
  };
  return map[id] ?? 'rest';
}

/** Letter → shape for character alignment or any text source (null = keep the current shape). */
export function mapChar(ch: string): Viseme | null {
  ch = (ch || '').toLowerCase();
  if (!ch) return null;
  if (ch === 'a') return 'AA';
  if ('eiy'.includes(ch)) return 'E';
  if (ch === 'o') return 'O';
  if ('uw'.includes(ch)) return 'U';
  if ('mbp'.includes(ch)) return 'M';
  if ('fv'.includes(ch)) return 'F';
  if ('ldntsrzgkjqxch'.includes(ch)) return 'L';
  return null;
}

export class TalkingAvatar {
  private colors: AvatarColors;
  private reduceMotion: boolean;
  private cur: MouthShape = { ...VIS.rest };
  private target: MouthShape = { ...VIS.rest };
  private mood: MoodShape = MOOD.neutral;
  private smileCur = 0;
  private yaw = 0; private pitch = 0; private nodImpulse = 0;
  private browLift = 0; private browLiftT = 0;
  private nextBlink = 2 + Math.random() * 3; private blinkPhase = -1;
  private speaking = false;
  private onState: (speaking: boolean) => void;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private raf = 0;
  private audioRaf = 0;
  private detachAudio: (() => void) | null = null;
  private last = performance.now();
  private el: Record<(typeof HOOKS)[number], SVGElement>;

  constructor(mount: HTMLElement, opts: { colors?: Partial<AvatarColors>; reduceMotion?: boolean; onState?: (speaking: boolean) => void } = {}) {
    this.colors = { ...DEFAULT_COLORS, ...(opts.colors || {}) };
    this.reduceMotion = opts.reduceMotion ?? matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.onState = opts.onState || (() => {});
    mount.innerHTML = this.svgTemplate();
    this.el = Object.fromEntries(HOOKS.map(k => [k, mount.querySelector(`[data-id="${k}"]`)])) as Record<(typeof HOOKS)[number], SVGElement>;
    this.raf = requestAnimationFrame(t => this.tick(t));
  }

  /* ---------- public API ---------- */

  setViseme(name: Viseme, amount = 1) {
    const v = VIS[name] || VIS.rest;
    this.target = { w: v.w, o: v.o * amount, r: v.r, t: v.t };
  }
  setMood(name: Mood) { this.mood = MOOD[name] || MOOD.neutral; }
  nod(strength = 2.2) { this.nodImpulse += strength; }
  raiseBrows() { this.browLiftT = 1; }
  /** Off closes the mouth at once — call it on barge-in. */
  setSpeaking(on: boolean) {
    if (!on) { this.clearTimers(); this.setViseme('rest'); }
    if (this.speaking === on) return;
    this.speaking = on;
    this.onState(on);
  }

  /**
   * Live audio already flowing through an AudioContext: `node` is analysed and passed on to
   * `speakers` (pass the context's real destination; omit when something else plays it).
   */
  attachNode(ctx: AudioContext, node: AudioNode, speakers?: AudioNode) {
    this.detachAudio?.();
    const an = ctx.createAnalyser();
    an.fftSize = 1024;
    an.smoothingTimeConstant = 0.4;
    node.connect(an);
    if (speakers) node.connect(speakers);
    const data = new Uint8Array(an.fftSize), freq = new Uint8Array(an.frequencyBinCount);
    let shape: Viseme = 'AA', shapeAt = 0, silentSince = 0;
    const band = (a: number, b: number) => { let x = 0; for (let i = a; i < b; i++) x += freq[i]; return x / (b - a); };
    const loop = () => {
      an.getByteTimeDomainData(data);
      let s = 0;
      for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; s += v * v; }
      const open = Math.min(1, Math.max(0, (Math.sqrt(s / data.length) - 0.02) * 9));
      an.getByteFrequencyData(freq);
      const lo = band(2, 12), mid = band(12, 40), hi = band(40, 120), now = performance.now();
      if (open > 0.15 && now - shapeAt > 90) { shape = hi > mid * 0.9 ? 'E' : lo > mid * 1.3 ? 'O' : 'AA'; shapeAt = now; }
      if (open > 0.08) {
        silentSince = now;
        if (!this.speaking) this.setSpeaking(true);
        this.setViseme(shape, open);
      } else {
        this.setViseme('rest');
        if (this.speaking && now - silentSince > 600) this.setSpeaking(false);
      }
      this.audioRaf = requestAnimationFrame(loop);
    };
    loop();
    this.detachAudio = () => {
      cancelAnimationFrame(this.audioRaf);
      try { node.disconnect(an); } catch { /* already gone */ }
      this.detachAudio = null;
    };
  }

  /** No audio access: mouth a sentence at speaking pace from its letters, starting now. */
  speakAlong(sentence: string) {
    this.clearTimers();
    this.setSpeaking(true);
    const msPerChar = 1000 / CHARS_PER_SECOND;
    let at = 0;
    for (const word of sentence.split(/\s+/).filter(Boolean)) {
      for (const c of word) {
        const v = mapChar(c);
        if (!v) continue;
        const amp = 0.75 + Math.random() * 0.35;
        this.timers.push(setTimeout(() => this.setViseme(v, amp), at));
        at += msPerChar;
      }
      // The gap between words.
      this.timers.push(setTimeout(() => this.setViseme('rest'), at));
      at += msPerChar;
    }
    this.cueSentence(sentence, at);
  }

  /** Expression for a sentence that has just started being spoken (`durationMs` to its end; estimated if omitted). */
  cueSentence(sentence: string, durationMs = (sentence.length * 1000) / CHARS_PER_SECOND) {
    const end = sentence.trim().slice(-1);
    if (end === '?') { this.raiseBrows(); this.nodImpulse -= 1; }
    else if (end === '.' || end === '!') this.timers.push(setTimeout(() => this.nod(), durationMs));
  }

  /** Precise path: timestamped visemes [{t:ms, v:'AA', a:1}] relative to `startedAt` (performance.now()). */
  playVisemeTrack(track: VisemeEvent[], startedAt = performance.now()) {
    this.clearTimers();
    for (const ev of track) {
      const delay = Math.max(0, startedAt + ev.t - performance.now());
      this.timers.push(setTimeout(() => this.setViseme(ev.v, ev.a ?? 1), delay));
    }
  }

  stop() { this.detachAudio?.(); this.setSpeaking(false); }
  destroy() { this.stop(); cancelAnimationFrame(this.raf); }

  /* ---------- internals ---------- */

  private clearTimers() { this.timers.forEach(clearTimeout); this.timers = []; }

  private tick(now: number) {
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    const t = now / 1000;
    const k = 1 - Math.exp(-dt * 28);
    for (const p of ['w', 'o', 'r', 't'] as const) this.cur[p] += (this.target[p] - this.cur[p]) * k;
    this.smileCur += (this.mood.smile - this.smileCur) * (1 - Math.exp(-dt * 6));
    this.drawMouth();

    const amp = this.speaking ? 1.6 : 1;
    let yawT = Math.sin(t * 0.55) * 1.8 * amp + Math.sin(t * 1.7) * 0.4 * amp;
    let pitchT = Math.cos(t * 0.41) * 1.1 * amp + this.nodImpulse;
    this.nodImpulse *= Math.exp(-dt * 4);
    if (this.reduceMotion) { yawT = 0; pitchT = this.nodImpulse; }
    this.yaw += (yawT - this.yaw) * (1 - Math.exp(-dt * 3));
    this.pitch += (pitchT - this.pitch) * (1 - Math.exp(-dt * 3));
    this.el.head.setAttribute('transform', `translate(${this.yaw * 1.4} ${this.pitch * 1.2}) rotate(${this.yaw * 0.5} 210 300)`);
    this.el.body.setAttribute('transform', `translate(${this.yaw * 0.3} ${this.reduceMotion ? 0 : Math.sin(t * 0.9) * 0.8})`);
    this.el.pupils.setAttribute('transform', `translate(${this.yaw * 0.6} ${this.pitch * 0.3})`);

    this.browLift += (this.browLiftT - this.browLift) * (1 - Math.exp(-dt * 10));
    this.browLiftT *= Math.exp(-dt * 1.2);
    this.el.brows.setAttribute('transform', `translate(0 ${-this.browLift * 5 + this.mood.browY}) rotate(${this.mood.browTilt} 210 174)`);

    this.nextBlink -= dt;
    if (this.nextBlink <= 0 && this.blinkPhase < 0) { this.blinkPhase = 0; this.nextBlink = 2.5 + Math.random() * 3.5; }
    let lid = this.mood.lidTop;
    if (this.blinkPhase >= 0) {
      this.blinkPhase += dt / 0.16;
      lid = Math.max(lid, Math.sin(Math.min(1, this.blinkPhase) * Math.PI));
      if (this.blinkPhase >= 1) this.blinkPhase = -1;
    }
    this.el.lids.setAttribute('transform', `translate(0 187) scale(1 ${lid}) translate(0 -187)`);
    this.raf = requestAnimationFrame(t2 => this.tick(t2));
  }

  private drawMouth() {
    const CX = 210, CY = 248, { w, o, r } = this.cur, smile = this.smileCur;
    const half = w * (1 - r * 0.35), topY = CY - o * 0.35, botY = CY + o * 0.65, corner = CY - smile * 0.6;
    const lipTop = topY - 4 - r * 2, lipBot = botY + 4 + r;
    const outer = `M${CX - half - 3},${corner} Q${CX - half * 0.4},${lipTop} ${CX},${lipTop} Q${CX + half * 0.4},${lipTop} ${CX + half + 3},${corner} Q${CX + half * 0.5},${lipBot} ${CX},${lipBot} Q${CX - half * 0.5},${lipBot} ${CX - half - 3},${corner} Z`;
    const inner = `M${CX - half},${corner} Q${CX - half * 0.4},${topY} ${CX},${topY} Q${CX + half * 0.4},${topY} ${CX + half},${corner} Q${CX + half * 0.5},${botY} ${CX},${botY} Q${CX - half * 0.5},${botY} ${CX - half},${corner} Z`;
    this.el.lipOuter.setAttribute('d', outer);
    this.el.mouthInner.setAttribute('d', inner);
    this.el.mouthClipPath.setAttribute('d', inner);
    this.el.teethTop.setAttribute('y', String(topY - 1));
    this.el.teethTop.setAttribute('height', String(Math.max(0, 7 * this.cur.t)));
  }

  private svgTemplate() {
    const c = this.colors, uid = 'av' + Math.random().toString(36).slice(2, 7);
    return `<svg viewBox="0 0 420 420" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Assistant" style="display:block;width:100%;height:100%">
  <defs>
    <radialGradient id="${uid}w" cx="50%" cy="40%" r="70%"><stop offset="0" stop-color="${c.wall1}"/><stop offset="1" stop-color="${c.wall2}"/></radialGradient>
    <clipPath id="${uid}eL"><ellipse cx="178" cy="196" rx="15" ry="9"/></clipPath>
    <clipPath id="${uid}eR"><ellipse cx="242" cy="196" rx="15" ry="9"/></clipPath>
    <clipPath id="${uid}m"><path data-id="mouthClipPath" d=""/></clipPath>
  </defs>
  <rect width="420" height="420" fill="url(#${uid}w)"/>
  <rect x="0" y="330" width="420" height="4" fill="${c.stripe}" opacity=".55"/>
  <rect x="0" y="338" width="420" height="82" fill="${c.desk}" opacity=".6"/>
  <g data-id="body">
    <path d="M70 420 C70 350 120 318 170 312 L250 312 C300 318 350 350 350 420 Z" fill="${c.coat}"/>
    <path d="M190 312 L210 360 L230 312 Z" fill="${c.shirt}"/>
    <path d="M210 360 L198 420 L222 420 Z" fill="${c.stripe}"/>
    <circle cx="210" cy="330" r="4" fill="${c.stripe}"/>
    <g data-id="head">
      <path d="M186 270 L186 318 L234 318 L234 270 Z" fill="${c.skinShade}"/>
      <path d="M138 215 C130 140 160 100 210 98 C262 100 292 140 282 215 L282 250 L138 250 Z" fill="${c.hair}"/>
      <ellipse cx="146" cy="212" rx="9" ry="14" fill="${c.skinShade}"/><ellipse cx="274" cy="212" rx="9" ry="14" fill="${c.skinShade}"/>
      <path d="M148 190 C148 130 172 108 210 108 C248 108 272 130 272 190 C272 236 250 282 210 284 C170 282 148 236 148 190 Z" fill="${c.skin}"/>
      <ellipse cx="172" cy="226" rx="12" ry="7" fill="${c.cheek}" opacity=".35"/><ellipse cx="248" cy="226" rx="12" ry="7" fill="${c.cheek}" opacity=".35"/>
      <path d="M146 180 C150 120 180 100 212 100 C250 100 276 126 276 170 C262 140 238 136 218 150 C200 134 170 136 146 180 Z" fill="${c.hair}"/>
      <g data-id="brows">
        <path d="M162 176 C170 168 186 168 194 174" stroke="${c.hair}" stroke-width="4" stroke-linecap="round" fill="none"/>
        <path d="M226 174 C234 168 250 168 258 176" stroke="${c.hair}" stroke-width="4" stroke-linecap="round" fill="none"/>
      </g>
      <g>
        <ellipse cx="178" cy="196" rx="15" ry="9" fill="#fff"/><ellipse cx="242" cy="196" rx="15" ry="9" fill="#fff"/>
        <g data-id="pupils">
          <circle cx="179" cy="197" r="6" fill="${c.eye}" clip-path="url(#${uid}eL)"/><circle cx="243" cy="197" r="6" fill="${c.eye}" clip-path="url(#${uid}eR)"/>
          <circle cx="181" cy="195" r="1.8" fill="#fff" clip-path="url(#${uid}eL)"/><circle cx="245" cy="195" r="1.8" fill="#fff" clip-path="url(#${uid}eR)"/>
        </g>
        <g data-id="lids" transform="translate(0 187) scale(1 0) translate(0 -187)">
          <ellipse cx="178" cy="196" rx="16" ry="10" fill="${c.skin}"/><ellipse cx="242" cy="196" rx="16" ry="10" fill="${c.skin}"/>
        </g>
        <path d="M163 192 C170 184 186 184 193 191" stroke="${c.hair}" stroke-width="2" fill="none" stroke-linecap="round"/>
        <path d="M227 191 C234 184 250 184 257 192" stroke="${c.hair}" stroke-width="2" fill="none" stroke-linecap="round"/>
      </g>
      <path d="M206 214 C202 226 204 232 212 232" stroke="${c.skinShade}" stroke-width="2.5" fill="none" stroke-linecap="round"/>
      <g>
        <path data-id="lipOuter" d="" fill="${c.lips}"/>
        <path data-id="mouthInner" d="" fill="${c.mouth}"/>
        <g clip-path="url(#${uid}m)"><rect data-id="teethTop" x="186" y="236" width="48" height="7" rx="2" fill="${c.teeth}"/></g>
      </g>
    </g>
  </g>
</svg>`;
  }
}
